import {
  DateInput,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import {
  MARITAL_STATUS_OPTIONS,
  SEX_OPTIONS,
} from '@/features/client-quick-plan';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

const PARTNER_ELIGIBLE_STATUSES = ['Married', 'Partnered'];

interface QuickPlanPersonFieldsProps<T extends FieldValues> {
  control: Control<T>;
  fieldPrefix?: 'partner.';
  onMaritalStatusChange?: (value: string) => void;
}

export function QuickPlanPersonFields<T extends FieldValues>({
  control,
  fieldPrefix,
  onMaritalStatusChange,
}: QuickPlanPersonFieldsProps<T>) {
  function fieldName(name: string): FieldPath<T> {
    return (fieldPrefix ? `${fieldPrefix}${name}` : name) as FieldPath<T>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name={fieldName('firstName')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>First name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  placeholder="Jane"
                  {...field}
                  value={field.value as string}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={fieldName('lastName')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  placeholder="Doe"
                  {...field}
                  value={field.value as string}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name={fieldName('birthDate')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Date of birth <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <DateInput
                  value={field.value as Date | undefined}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName('sex')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sex <span className="text-destructive">*</span></FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value as string}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-99">
                  {SEX_OPTIONS.map(option => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={control}
        name={fieldName('smokingStatus')}
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel>Smoking status <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value as string}
                className="flex gap-4"
              >
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="never" />
                  </FormControl>
                  <FormLabel className="font-normal">Never</FormLabel>
                </FormItem>
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="former" />
                  </FormControl>
                  <FormLabel className="font-normal">Former</FormLabel>
                </FormItem>
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="current" />
                  </FormControl>
                  <FormLabel className="font-normal">Current</FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name={fieldName('zipCode')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Zip code <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  placeholder="12345"
                  maxLength={5}
                  {...field}
                  value={field.value as string}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName('maritalStatus')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Marital status <span className="text-destructive">*</span></FormLabel>
              <Select
                onValueChange={onMaritalStatusChange ?? field.onChange}
                value={field.value as string}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-99">
                  {MARITAL_STATUS_OPTIONS.map(option => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      disabled={
                        fieldPrefix === 'partner.' &&
                        !PARTNER_ELIGIBLE_STATUSES.includes(option.value)
                      }
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
